import { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";

import { Button } from "../../../components/ui/Button";
import { t } from "../../../lib/i18n";

interface QRCodeBoxProps {
  /** The payload encoded into the QR. Usually JSON.stringify of the bootstrap payload. */
  value: string;
  /** Filename for the downloaded PNG, without extension. */
  filename: string;
  /** Display size of the rendered QR (pixels). Defaults to 224. */
  size?: number;
}

/** SVG-based QR code rendering with a "Download PNG" helper. */
export function QRCodeBox({ value, filename, size = 224 }: QRCodeBoxProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const onDownload = async () => {
    const svg = wrapperRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const img = new Image();
    const dataUrl: string = await new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const scale = 4;
          canvas.width = size * scale;
          canvas.height = size * scale;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("canvas_unavailable"));
            return;
          }
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          reject(err);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("image_load_failed"));
      };
      img.src = url;
    });

    const anchor = document.createElement("a");
    anchor.href = dataUrl;
    anchor.download = `${filename}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
      <div
        ref={wrapperRef}
        style={{
          padding: 12,
          background: "var(--surface-1, #fff)",
          border: "1px solid var(--border, #e2e2e2)",
          borderRadius: 8,
        }}
      >
        <QRCodeSVG value={value} size={size} level="M" includeMargin />
      </div>
      <Button variant="secondary" size="sm" onClick={() => void onDownload()}>
        {t("admin.bootstrap.actions.downloadQr")}
      </Button>
    </div>
  );
}
